import { durationEnumToMinutes } from './bookingUtils';

/**
 * How long a booking takes (SF 73: "2h 15m, one after the other with one
 * groomer"). Every cart line is a pet's service at the option picked for it,
 * and the API books the lines one after the other with one groomer
 * (`createStoreAppointment` starts each where the last ended), so the run is
 * the sum of the lines.
 *
 * Per line, never per service: two pets on one service at different sizes
 * (a small dog's Full groom and a big dog's) each keep their own length. The
 * old time step kept one length per service, so the second pet's replaced the
 * first's and the times offered didn't fit the booking made.
 */
export interface DurationLine {
  pricingDetails?: { duration?: number | null } | null;
  /** A booked line's own length (a reschedule, G21): it wins over the option's. */
  minutes?: number | null;
}

/** A line's length in minutes, read as the create call reads it, or as it was booked. */
export const lineMinutes = (line: DurationLine): number =>
  typeof line?.minutes === 'number' && line.minutes > 0
    ? line.minutes
    : durationEnumToMinutes(line?.pricingDetails?.duration ?? undefined);

/** The whole run, in minutes. */
export const bookingMinutes = (
  petServices: Record<string, DurationLine[] | undefined | null>
): number =>
  Object.values(petServices).reduce(
    (total, lines) => total + (lines || []).reduce((n, line) => n + lineMinutes(line), 0),
    0
  );

/** "45m", "1h", "2h 15m". */
export const formatRun = (minutes: number): string => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
};
